import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Hidden,
  Grid,
  Typography,
  useTheme,
  CircularProgress,
  FormControlLabel,
} from "@material-ui/core";
import { Edit, Add } from "@material-ui/icons";
import { useRouteMatch } from "react-router-dom";
import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import withAuthority from "../../../../components/Auth/withAuthority";
import {
  createModifierGroup,
  fetchModifierGroupList,
  updateModifierGroup,
} from "../../../../services/menuApp/ModifierGroups";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { getCookie } from "../../../../utils/cookies";
import PageHeaderMobile from "../../../../components/common/PageHeader/PageHeaderMobile";
import CardCommon from "../../../../components/card/CardCommon";
import DialogCommon from "../../../../components/dialogs/DialogCommon";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { convertDateFormat } from "../../../../utils/ConvertDateTimeFormat";
import { AxiosError } from "axios";
import ValidationMessage from "../../../../components/validation/ValidationMessage";
import TextfieldDefaultNew from "../../../../components/textField/TextfieldDefaultNew";
import { fetchProductConfigInfo } from "../../../../services/menuApp/printerMenuService";
import { saveLogs } from "../../../../utils/firebase/logs";
import SwitchCommon from "../../../../components/switches/SwitchCommon";

const ModifierGroups: React.FunctionComponent = () => {
  const [modifierGroupsList, setModifierGroupsList] = useState<any[]>([]);
  const [filteredModifierGroupsList, setFilteredModifierGroupsList] = useState<
    any[]
  >([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isProductAddModalOpen, setIsProductAddModalOpen] = useState(false);
  const [isProductEditModalOpen, setIsProductEditModalOpen] = useState(false);
  const [newlyCreatedModifier, setNewlyCreatedModifier] = useState<any>(null);
  const [selectedModifierGroup, setSelectedModifierGroup] = useState<any>("");
  const [mappedModifierGroupData, setMappedModifierGroupData] =
    useState<any>(null);
  const [createErrors, setCreateErrors] = useState<{
    name?: string;
    priceChangePoint?: string;
    min?: string;
    max?: string;
  }>({});
  const [editErrors, setEditErrors] = useState<{
    name?: string;
    priceChangePoint?: string;
    min?: string;
    max?: string;
  }>({});
  const [productsVersion, setProductsVersion] = useState<number>(1);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [newModifierGroup, setNewModifierGroup] = useState<Partial<any>>({
    name: "",
    priceChangePoint: 1,
    min: 0,
    max: 10,
    optionType: false,
    groupTemplateRef: "",
    nestedId: "",
  });
  const [editModifierGroup, setEditModifierGroup] = useState<Partial<any>>({
    name: "",
    priceChangePoint: 1,
    min: 0,
    max: 10,
    optionType: false,
    groupTemplateRef: "",
    nestedId: "",
  });

  const match: any = useRouteMatch();
  const locationId = match.params.locationId;
  const token = getCookie("idToken");
  const theme: any = useTheme();

  // Function to check if nestedId should be shown (-99, "", null, or 0)
  const shouldShowModifierGroup = (nestedId: any): boolean => {
    return (
      nestedId === "-99" ||
      nestedId === -99 ||
      nestedId === "" ||
      nestedId === null ||
      nestedId === 0
    );
  };

  // Function to check if groupTemplateRef is editable (-99, "", or null)
  const isGroupTemplateRefEditable = (groupTemplateRef: any): boolean => {
    return (
      groupTemplateRef === -99 ||
      groupTemplateRef === "-99" ||
      groupTemplateRef === "" ||
      groupTemplateRef === null
    );
  };

  // Function to find modifier group by ID
  const findModifierGroupById = (id: number) => {
    return modifierGroupsList.find((group) => group.id === id);
  };

  // Validate create form
  const validateCreateForm = (): boolean => {
    const errors: {
      name?: string;
      priceChangePoint?: string;
      min?: string;
      max?: string;
    } = {};

    if (!newModifierGroup.name?.trim()) {
      errors.name = "Please enter a name";
    }

    const pricePoint = parseInt(newModifierGroup.priceChangePoint);
    if (isNaN(pricePoint) || pricePoint < 0) {
      errors.priceChangePoint = "Price Change Point cannot be less than 0";
    }

    const minVal = parseInt(newModifierGroup.min);
    if (isNaN(minVal) || minVal < 0) {
      errors.min = "Min cannot be less than 0";
    }

    const maxVal = parseInt(newModifierGroup.max);
    if (isNaN(maxVal) || maxVal < 1) {
      errors.max = "Max must be at least 1";
    } else if (minVal > maxVal) {
      errors.max = "Max cannot be less than Min";
    }

    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate edit form
  const validateEditForm = (): boolean => {
    const errors: {
      name?: string;
      priceChangePoint?: string;
      min?: string;
      max?: string;
    } = {};

    if (!editModifierGroup.name?.trim()) {
      errors.name = "Please enter a name";
    }

    const pricePoint = parseInt(editModifierGroup.priceChangePoint);
    if (isNaN(pricePoint) || pricePoint < 0) {
      errors.priceChangePoint = "Price Change Point cannot be less than 0";
    }

    const minVal = parseInt(editModifierGroup.min);
    if (isNaN(minVal) || minVal < 0) {
      errors.min = "Min cannot be less than 0";
    }

    const maxVal = parseInt(editModifierGroup.max);
    if (isNaN(maxVal) || maxVal < 1) {
      errors.max = "Max must be at least 1";
    } else if (minVal > maxVal) {
      errors.max = "Max cannot be less than Min";
    }

    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle price change point change
  const handlePriceChangePointChange = (
    value: string,
    isEdit: boolean = false,
  ) => {
    if (value === "") {
      if (isEdit) {
        setEditModifierGroup((prev: any) => ({
          ...prev,
          priceChangePoint: "",
        }));
      } else {
        setNewModifierGroup((prev: any) => ({
          ...prev,
          priceChangePoint: "",
        }));
      }
      return;
    }

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      if (isEdit) {
        setEditModifierGroup((prev: any) => ({
          ...prev,
          priceChangePoint: numValue,
        }));
      } else {
        setNewModifierGroup((prev: any) => ({
          ...prev,
          priceChangePoint: numValue,
        }));
      }
    } else if (!isNaN(numValue) && numValue < 0) {
      if (isEdit) {
        setEditModifierGroup((prev: any) => ({
          ...prev,
          priceChangePoint: 0,
        }));
      } else {
        setNewModifierGroup((prev: any) => ({
          ...prev,
          priceChangePoint: 0,
        }));
      }
    }
  };

  // Handle min value change
  const handleMinChange = (value: string, isEdit: boolean = false) => {
    if (value === "") {
      if (isEdit) {
        setEditModifierGroup((prev: any) => ({
          ...prev,
          min: "",
        }));
      } else {
        setNewModifierGroup((prev: any) => ({
          ...prev,
          min: "",
        }));
      }
      return;
    }

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      if (isEdit) {
        const currentMax = editModifierGroup.max ?? 10;
        if (numValue > currentMax) {
          setEditModifierGroup((prev: any) => ({
            ...prev,
            min: currentMax,
            max: currentMax,
          }));
        } else {
          setEditModifierGroup((prev: any) => ({
            ...prev,
            min: numValue,
          }));
        }
      } else {
        const currentMax = newModifierGroup.max ?? 10;
        if (numValue > currentMax) {
          setNewModifierGroup((prev: any) => ({
            ...prev,
            min: currentMax,
            max: currentMax,
          }));
        } else {
          setNewModifierGroup((prev: any) => ({
            ...prev,
            min: numValue,
          }));
        }
      }
    }
  };

  // Handle max value change
  const handleMaxChange = (value: string, isEdit: boolean = false) => {
    if (value === "") {
      if (isEdit) {
        setEditModifierGroup((prev: any) => ({
          ...prev,
          max: "",
        }));
      } else {
        setNewModifierGroup((prev: any) => ({
          ...prev,
          max: "",
        }));
      }
      return;
    }

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1) {
      if (isEdit) {
        const currentMin = editModifierGroup.min ?? 0;
        if (numValue < currentMin) {
          setEditModifierGroup((prev: any) => ({
            ...prev,
            min: numValue,
            max: numValue,
          }));
        } else {
          setEditModifierGroup((prev: any) => ({
            ...prev,
            max: numValue,
          }));
        }
      } else {
        const currentMin = newModifierGroup.min ?? 0;
        if (numValue < currentMin) {
          setNewModifierGroup((prev: any) => ({
            ...prev,
            min: numValue,
            max: numValue,
          }));
        } else {
          setNewModifierGroup((prev: any) => ({
            ...prev,
            max: numValue,
          }));
        }
      }
    }
  };

  // Validate before submit
  const validateAndGetPriceChangePoint = (value: any): number => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) return 0;
    return numValue;
  };

  const validateAndGetMin = (value: any): number => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) return 0;
    return numValue;
  };

  const validateAndGetMax = (value: any): number => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1) return 10;
    return numValue;
  };

  // Get product configuration to check version
  const getProductConfiguration = useCallback(async () => {
    if (!locationId) return;

    try {
      const { data } = await fetchProductConfigInfo(locationId);
      if (data && data.data) {
        const { productsVersion } = data.data;
        setProductsVersion(productsVersion || 1);
      }
    } catch (error) {
      const err: any = error as AxiosError;
      if (err.response?.status !== 404) {
        console.error("Error fetching product configuration:", error);
      }
    } finally {
      setIsLoadingConfig(false);
    }
  }, [locationId]);

  // Get all modifier groups
  const getModifierGroupList = useCallback(async () => {
    if (!locationId || !token) return;

    setIsLoading(true);
    try {
      const res = await fetchModifierGroupList(locationId, token);
      const allModifierGroups = res.data.data || [];
      const filtered = allModifierGroups.filter((group: any) =>
        shouldShowModifierGroup(group.nestedId),
      );
      setModifierGroupsList(allModifierGroups);
      setFilteredModifierGroupsList(filtered);
      setError("");
    } catch (error) {
      const err: any = error as AxiosError;
      setError(err?.response?.data?.message || ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [locationId, token]);

  // Update modifier group
  const handleUpdateModifierGroup = async () => {
    if (productsVersion === 1) {
      setError("Modifier groups cannot be edited when product version is 1");
      return;
    }

    if (!editModifierGroup) return;

    if (!validateEditForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData: any = {
        id: selectedModifierGroup.id,
        locationId: locationId,
        name: editModifierGroup.name.trim(),
        priceChangePoint: validateAndGetPriceChangePoint(
          editModifierGroup.priceChangePoint,
        ),
        min: validateAndGetMin(editModifierGroup.min),
        max: validateAndGetMax(editModifierGroup.max),
        optionType: editModifierGroup.optionType ? 1 : 0,
        groupTemplateRef: editModifierGroup.groupTemplateRef || "",
        nestedId: editModifierGroup.nestedId || "",
        version: selectedModifierGroup.version,
      };

      await updateModifierGroup(locationId, updateData, token);
      await getModifierGroupList();
      setIsEditModalOpen(false);
      setSelectedModifierGroup("");
      setMappedModifierGroupData(null);
      setEditModifierGroup({
        name: "",
        priceChangePoint: 1,
        min: 0,
        max: 10,
        optionType: false,
        groupTemplateRef: "",
        nestedId: "",
      });
      setEditErrors({});
      setError("");

      saveLogs(
        {
          message: "Modifier Group Updated",
          summary: `Updated modifier group: ${editModifierGroup.name}`,
          type: "info",
          code: "modifier-group-update",
          metadata: { changedBy: "" },
        },
        "menu",
        locationId,
      );
    } catch (error) {
      const err: any = error as AxiosError;
      setError(err?.response?.data?.message || ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create new modifier group
  const handleCreateModifierGroup = async () => {
    if (productsVersion === 1) {
      setError("Modifier groups cannot be created when product version is 1");
      return;
    }

    if (!validateCreateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const createData: any = {
        name: newModifierGroup.name.trim(),
        priceChangePoint: validateAndGetPriceChangePoint(
          newModifierGroup.priceChangePoint,
        ),
        min: validateAndGetMin(newModifierGroup.min),
        max: validateAndGetMax(newModifierGroup.max),
        optionType: newModifierGroup.optionType ? 1 : 0,
        groupTemplateRef: newModifierGroup.groupTemplateRef || "",
        nestedId: "",
        locationId: locationId,
      };

      const response = await createModifierGroup(locationId, createData, token);
      await getModifierGroupList();

      // Close create modal
      setIsCreateModalOpen(false);

      // Set the newly created modifier data
      const createdModifier = response.data.data;
      setNewlyCreatedModifier(createdModifier);

      // Open the product add modal with the created data
      setIsProductAddModalOpen(true);

      setNewModifierGroup({
        name: "",
        priceChangePoint: 1,
        min: 0,
        max: 10,
        optionType: false,
        groupTemplateRef: "",
        nestedId: "",
      });
      setCreateErrors({});
      setError("");

      saveLogs(
        {
          message: "Modifier Group Created",
          summary: `Created new modifier group: ${newModifierGroup.name}`,
          type: "info",
          code: "modifier-group-create",
          metadata: { changedBy: "" },
        },
        "menu",
        locationId,
      );
    } catch (error) {
      const err: any = error as AxiosError;
      setError(err?.response?.data?.message || ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle add product from create modal
  const handleAddProduct = () => {
    // This will be implemented later with actual product add logic
    console.log("Add product for modifier:", newlyCreatedModifier);
    setIsProductAddModalOpen(false);
    setNewlyCreatedModifier(null);
  };

  // Handle edit product from edit modal
  const handleEditProduct = () => {
    // This will be implemented later with actual product edit logic
    console.log("Edit product for modifier:", selectedModifierGroup);
    setIsProductEditModalOpen(false);
  };

  // Handle open edit product modal from edit screen
  const handleOpenEditProductModal = () => {
    setIsProductEditModalOpen(true);
  };

  // Handle edit click
  const handleEditClick = (modifierGroup: any) => {
    if (productsVersion === 1) {
      setError("Modifier groups cannot be edited when product version is 1");
      return;
    }

    setSelectedModifierGroup(modifierGroup);

    if (!isGroupTemplateRefEditable(modifierGroup.groupTemplateRef)) {
      const mappedData = findModifierGroupById(modifierGroup.groupTemplateRef);

      if (mappedData) {
        setMappedModifierGroupData(mappedData);
        setEditModifierGroup({
          name: mappedData.name || "",
          priceChangePoint: mappedData.priceChangePoint ?? 1,
          min: mappedData.min ?? 0,
          max: mappedData.max ?? 10,
          optionType: mappedData.optionType === 1,
          groupTemplateRef: mappedData.groupTemplateRef || "",
          nestedId: mappedData.nestedId || "",
        });
      } else {
        setMappedModifierGroupData(null);
        setEditModifierGroup({
          name: "No matching modifier group found",
          priceChangePoint: 1,
          min: 0,
          max: 10,
          optionType: false,
          groupTemplateRef: modifierGroup.groupTemplateRef || "",
          nestedId: "",
        });
      }
    } else {
      setMappedModifierGroupData(null);
      setEditModifierGroup({
        name: modifierGroup.name || "",
        priceChangePoint: modifierGroup.priceChangePoint ?? 1,
        min: modifierGroup.min ?? 0,
        max: modifierGroup.max ?? 10,
        optionType: modifierGroup.optionType === 1,
        groupTemplateRef: modifierGroup.groupTemplateRef || "",
        nestedId: modifierGroup.nestedId || "",
      });
    }

    setIsEditModalOpen(true);
  };

  useEffect(() => {
    document.title = "Menu - Modifier Groups";
    getProductConfiguration();
    getModifierGroupList();
  }, [getProductConfiguration, getModifierGroupList]);

  const isActionsDisabled = productsVersion === 1 || isLoadingConfig;
  const isEditModalDisabled = !isGroupTemplateRefEditable(
    selectedModifierGroup?.groupTemplateRef,
  );
  const hasMappedData = mappedModifierGroupData !== null;

  const getOptionTypeText = (optionType: number) => {
    return optionType === 1 ? "Active" : "Inactive";
  };

  // Modifier Group Card Component
  const ModifierGroupCard = ({ modifierGroup }: { modifierGroup: any }) => {
    return (
      <CardCommon backgroundColor="entity_background">
        <Grid container style={{ minHeight: "40px", placeItems: "center" }}>
          <Grid item xs={2} style={{ paddingLeft: "12px" }}>
            <Typography align="left">{modifierGroup.name || "-"}</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography align="left">
              {modifierGroup.priceChangePoint ?? "-"}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography align="left">
              {modifierGroup.min ?? "-"} / {modifierGroup.max ?? "-"}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography align="left">
              {getOptionTypeText(modifierGroup.optionType)}
            </Typography>
          </Grid>
          <Grid item xs={2} style={{ paddingRight: "12px" }}>
            <Typography align="left">
              {modifierGroup.created
                ? convertDateFormat(modifierGroup.created)
                : "-"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: "12px",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <ButtonCommon
              style={{
                fontSize: 11,
                minWidth: "28px",
                height: "28px",
              }}
              variant="contained"
              color="yellow"
              onClick={() => handleEditClick(modifierGroup)}
              isDisabled={isSubmitting || isActionsDisabled}
              title={
                isActionsDisabled
                  ? "Editing disabled when product version is 1"
                  : ""
              }
            >
              <Edit style={{ fontSize: "16px" }} />
            </ButtonCommon>
          </Grid>
        </Grid>
      </CardCommon>
    );
  };

  // Header Card Component
  const HeaderCard = () => (
    <CardCommon backgroundColor="table_header_background">
      <Grid container style={{ minHeight: "40px", placeItems: "center" }}>
        <Grid item xs={2} style={{ paddingLeft: "12px" }}>
          <Typography
            variant="body2"
            align="left"
            style={{ fontWeight: "bold" }}
          >
            Name
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography
            align="left"
            variant="body2"
            style={{ fontWeight: "bold" }}
          >
            Price Change Point
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography
            align="left"
            variant="body2"
            style={{ fontWeight: "bold" }}
          >
            Min/Max
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography
            align="left"
            variant="body2"
            style={{ fontWeight: "bold" }}
          >
            Option Type
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography
            align="left"
            variant="body2"
            style={{ fontWeight: "bold" }}
          >
            Created
          </Typography>
        </Grid>
        <Grid item xs={2} style={{ paddingRight: "12px", textAlign: "right" }}>
          <Typography
            align="right"
            variant="body2"
            style={{ fontWeight: "bold" }}
          >
            Actions
          </Typography>
        </Grid>
      </Grid>
    </CardCommon>
  );

  if (isLoadingConfig) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>

      <Box p={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="h5" style={{ fontWeight: "bold" }}>
              Modifier Groups
            </Typography>
          </Box>
          <ButtonCommon
            color="yellow"
            onClick={() => setIsCreateModalOpen(true)}
            isLoadingPage={isSubmitting}
            style={{ width: "280px" }}
            isDisabled={isActionsDisabled}
            title={
              isActionsDisabled
                ? "Creating disabled when product version is 1"
                : ""
            }
          >
            <Add style={{ marginRight: "4px" }} /> Create New Modifier Group
          </ButtonCommon>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <HeaderCard />
            {filteredModifierGroupsList.length === 0 && (
              <CardCommon backgroundColor="entity_background">
                <Box p={4} textAlign="center">
                  <Typography>
                    {isActionsDisabled
                      ? "No modifier groups found. Creating new modifier groups is disabled when product version is 1."
                      : "No modifier groups found"}
                  </Typography>
                </Box>
              </CardCommon>
            )}
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginTop: "8px",
              }}
            >
              {filteredModifierGroupsList.map((modifierGroup) => (
                <ModifierGroupCard
                  key={modifierGroup.id}
                  modifierGroup={modifierGroup}
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Create Modifier Group Modal */}
      <DialogCommon open={isCreateModalOpen} setOpen={setIsCreateModalOpen}>
        <Box p={4} style={{ minWidth: 500, maxWidth: 600 }}>
          <Typography variant="h6" style={{ marginTop: 0, marginBottom: 16 }}>
            Create New Modifier Group
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={8}>
                  <TextfieldDefaultNew
                    id="name"
                    name="name"
                    label="Name"
                    type="text"
                    value={newModifierGroup.name}
                    onChange={(e: any) =>
                      setNewModifierGroup((prev: any) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    margin="normal"
                    error={!!createErrors.name}
                  />
                  {createErrors.name && (
                    <ValidationMessage message={createErrors.name} />
                  )}
                </Grid>
                <Grid item xs={4}>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    <FormControlLabel
                      style={{ marginRight: "0px" }}
                      checked={newModifierGroup.optionType}
                      onChange={(e: any) =>
                        setNewModifierGroup((prev: any) => ({
                          ...prev,
                          optionType: e.target.checked,
                        }))
                      }
                      control={<SwitchCommon />}
                      label="Option Type"
                      labelPlacement="start"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={4}>
              <TextfieldDefaultNew
                id="priceChangePoint"
                name="priceChangePoint"
                label="Price Change Point"
                type="number"
                value={newModifierGroup.priceChangePoint}
                onChange={(e: any) =>
                  handlePriceChangePointChange(e.target.value, false)
                }
                margin="normal"
                error={!!createErrors.priceChangePoint}
              />
              {createErrors.priceChangePoint && (
                <ValidationMessage message={createErrors.priceChangePoint} />
              )}
            </Grid>

            <Grid item xs={4}>
              <TextfieldDefaultNew
                id="min"
                name="min"
                label="Min"
                type="number"
                value={newModifierGroup.min}
                onChange={(e: any) => handleMinChange(e.target.value, false)}
                margin="normal"
                error={!!createErrors.min}
              />
              {createErrors.min && (
                <ValidationMessage message={createErrors.min} />
              )}
            </Grid>

            <Grid item xs={4}>
              <TextfieldDefaultNew
                id="max"
                name="max"
                label="Max"
                type="number"
                value={newModifierGroup.max}
                onChange={(e: any) => handleMaxChange(e.target.value, false)}
                margin="normal"
                error={!!createErrors.max}
              />
              {createErrors.max && (
                <ValidationMessage message={createErrors.max} />
              )}
            </Grid>

            <Grid item xs={12}>
              <TextfieldDefaultNew
                id="groupTemplateRef"
                name="groupTemplateRef"
                label="Group Template Ref"
                type="number"
                value={newModifierGroup.groupTemplateRef || ""}
                onChange={(e: any) =>
                  setNewModifierGroup((prev: any) => ({
                    ...prev,
                    groupTemplateRef: e.target.value
                      ? parseInt(e.target.value)
                      : "",
                  }))
                }
                margin="normal"
              />
            </Grid>
          </Grid>

          <Box
            display="flex"
            justifyContent="flex-end"
            style={{ gap: "2px" }}
            mt={3}
          >
            <ButtonCommon
              onClick={() => {
                setIsCreateModalOpen(false);
                setCreateErrors({});
                setNewModifierGroup({
                  name: "",
                  priceChangePoint: 1,
                  min: 0,
                  max: 10,
                  optionType: false,
                  groupTemplateRef: "",
                  nestedId: "",
                });
              }}
              color="yellow"
              isDisabled={isSubmitting}
              style={{ width: "100px", marginRight: "12px" }}
            >
              Cancel
            </ButtonCommon>
            <ButtonCommon
              onClick={handleCreateModifierGroup}
              color="yellow"
              isLoadingPage={isSubmitting}
              style={{ width: "100px" }}
              isDisabled={productsVersion === 1}
            >
              Create
            </ButtonCommon>
          </Box>
        </Box>
      </DialogCommon>

      {/* Product Add Modal - Shows after create */}
      <DialogCommon
        open={isProductAddModalOpen}
        setOpen={setIsProductAddModalOpen}
      >
        <Box p={4} style={{ minWidth: 500, maxWidth: 600 }}>
          <Typography variant="h6" style={{ marginTop: 0, marginBottom: 16 }}>
            Add Product to Modifier Group
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}
          >
            Modifier Group "{newlyCreatedModifier?.name}" created successfully.
            You can now add products to this modifier group.
          </Typography>

          {/* Product add form will be implemented here later */}
          <Box p={3} textAlign="center">
            <Typography color="textSecondary">
              Product selection form will be added here
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            style={{ gap: "2px" }}
            mt={3}
          >
            <ButtonCommon
              onClick={() => {
                setIsProductAddModalOpen(false);
                setNewlyCreatedModifier(null);
              }}
              color="yellow"
              style={{ width: "100px", marginRight: "12px" }}
            >
              Cancel
            </ButtonCommon>
            <ButtonCommon
              onClick={handleAddProduct}
              color="yellow"
              style={{ width: "100px" }}
            >
              Add Product
            </ButtonCommon>
          </Box>
        </Box>
      </DialogCommon>

      {/* Edit Modifier Group Modal */}
      <DialogCommon open={isEditModalOpen} setOpen={setIsEditModalOpen}>
        <Box p={4} style={{ minWidth: 500, maxWidth: 600 }}>
          <Typography variant="h6" style={{ marginTop: 0, marginBottom: 16 }}>
            Edit Modifier Group
            {selectedModifierGroup?.groupTemplateRef &&
              !isGroupTemplateRefEditable(
                selectedModifierGroup.groupTemplateRef,
              ) && (
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  {hasMappedData
                    ? `Showing data from linked modifier group ID: ${selectedModifierGroup.groupTemplateRef}`
                    : `No matching modifier group found for ID: ${selectedModifierGroup.groupTemplateRef}`}
                </Typography>
              )}
          </Typography>

          {hasMappedData ||
          isGroupTemplateRefEditable(
            selectedModifierGroup?.groupTemplateRef,
          ) ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={8}>
                    <TextfieldDefaultNew
                      id="edit-name"
                      name="edit-name"
                      label="Name"
                      type="text"
                      value={editModifierGroup.name}
                      onChange={(e: any) =>
                        setEditModifierGroup((prev: any) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      margin="normal"
                      disabled={isEditModalDisabled}
                      error={!!editErrors.name}
                    />
                    {editErrors.name && (
                      <ValidationMessage message={editErrors.name} />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <FormControlLabel
                        style={{ marginRight: "0px" }}
                        checked={editModifierGroup.optionType}
                        onChange={(e: any) =>
                          setEditModifierGroup((prev: any) => ({
                            ...prev,
                            optionType: e.target.checked,
                          }))
                        }
                        disabled={isEditModalDisabled}
                        control={<SwitchCommon />}
                        label="Option Type"
                        labelPlacement="start"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={4}>
                <TextfieldDefaultNew
                  id="edit-priceChangePoint"
                  name="edit-priceChangePoint"
                  label="Price Change Point"
                  type="number"
                  value={editModifierGroup.priceChangePoint}
                  onChange={(e: any) =>
                    handlePriceChangePointChange(e.target.value, true)
                  }
                  margin="normal"
                  disabled={isEditModalDisabled}
                  error={!!editErrors.priceChangePoint}
                />
                {editErrors.priceChangePoint && (
                  <ValidationMessage message={editErrors.priceChangePoint} />
                )}
              </Grid>

              <Grid item xs={4}>
                <TextfieldDefaultNew
                  id="edit-min"
                  name="edit-min"
                  label="Min"
                  type="number"
                  value={editModifierGroup.min}
                  onChange={(e: any) => handleMinChange(e.target.value, true)}
                  margin="normal"
                  disabled={isEditModalDisabled}
                  error={!!editErrors.min}
                />
                {editErrors.min && (
                  <ValidationMessage message={editErrors.min} />
                )}
              </Grid>

              <Grid item xs={4}>
                <TextfieldDefaultNew
                  id="edit-max"
                  name="edit-max"
                  label="Max"
                  type="number"
                  value={editModifierGroup.max}
                  onChange={(e: any) => handleMaxChange(e.target.value, true)}
                  margin="normal"
                  disabled={isEditModalDisabled}
                  error={!!editErrors.max}
                />
                {editErrors.max && (
                  <ValidationMessage message={editErrors.max} />
                )}
              </Grid>

              <Grid item xs={12}>
                <TextfieldDefaultNew
                  id="edit-groupTemplateRef"
                  name="edit-groupTemplateRef"
                  label="Group Template Ref"
                  type="number"
                  value={editModifierGroup.groupTemplateRef || ""}
                  onChange={(e: any) =>
                    setEditModifierGroup((prev: any) => ({
                      ...prev,
                      groupTemplateRef: e.target.value
                        ? parseInt(e.target.value)
                        : "",
                    }))
                  }
                  margin="normal"
                  disabled={
                    !isGroupTemplateRefEditable(
                      selectedModifierGroup?.groupTemplateRef,
                    )
                  }
                />
              </Grid>
            </Grid>
          ) : (
            <Box p={3} textAlign="center">
              <Typography color="error">
                No matching modifier group found for ID:{" "}
                {selectedModifierGroup?.groupTemplateRef}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 8 }}
              >
                Please check the Group Template Reference ID or contact support.
              </Typography>
            </Box>
          )}

          <Box
            display="flex"
            justifyContent="flex-end"
            style={{ gap: "2px" }}
            mt={3}
          >
            <ButtonCommon
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedModifierGroup("");
                setMappedModifierGroupData(null);
                setEditErrors({});
                setEditModifierGroup({
                  name: "",
                  priceChangePoint: 1,
                  min: 0,
                  max: 10,
                  optionType: false,
                  groupTemplateRef: "",
                  nestedId: "",
                });
              }}
              color="yellow"
              isDisabled={isSubmitting}
              style={{ width: "100px", marginRight: "12px" }}
            >
              Cancel
            </ButtonCommon>
            {(hasMappedData ||
              isGroupTemplateRefEditable(
                selectedModifierGroup?.groupTemplateRef,
              )) && (
              <>
                <ButtonCommon
                  onClick={handleUpdateModifierGroup}
                  color="yellow"
                  isLoadingPage={isSubmitting}
                  style={{ width: "100px", marginRight: "12px" }}
                  isDisabled={productsVersion === 1 || isEditModalDisabled}
                >
                  Save
                </ButtonCommon>
                <ButtonCommon
                  onClick={handleOpenEditProductModal}
                  color="yellow"
                  style={{ width: "160px" }}
                  isDisabled={isEditModalDisabled}
                >
                  Edit Product
                </ButtonCommon>
              </>
            )}
          </Box>
        </Box>
      </DialogCommon>

      {/* Product Edit Modal - Shows from edit screen */}
      <DialogCommon
        open={isProductEditModalOpen}
        setOpen={setIsProductEditModalOpen}
      >
        <Box p={4} style={{ minWidth: 500, maxWidth: 600 }}>
          <Typography variant="h6" style={{ marginTop: 0, marginBottom: 16 }}>
            Edit Products for Modifier Group
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}
          >
            Modifier Group: "{selectedModifierGroup?.name}"
          </Typography>

          {/* Product edit form will be implemented here later */}
          <Box p={3} textAlign="center">
            <Typography color="textSecondary">
              Product edit form will be added here
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            style={{ gap: "2px" }}
            mt={3}
          >
            <ButtonCommon
              onClick={() => {
                setIsProductEditModalOpen(false);
              }}
              color="yellow"
              style={{ width: "100px", marginRight: "12px" }}
            >
              Cancel
            </ButtonCommon>
            <ButtonCommon
              onClick={handleEditProduct}
              color="yellow"
              style={{ width: "100px" }}
            >
              Update Product
            </ButtonCommon>
          </Box>
        </Box>
      </DialogCommon>

      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(ModifierGroups, Authorities.MENU_READ);
